import 'keen-slider/keen-slider.min.css';
import KeenSlider from 'keen-slider';

import './slider.scss';

// Docs: https://keen-slider.io/docs
// Gist: https://gist.github.com/devballers/060f9e3285f6cedce3a27e2a6b2beb4e

export class Slider {
    constructor(target, options) {
        this.classes = {
            slider: 'js-slider',
            sliderFade: 'slider--fade',
            item: 'slider__item',
            keenSlider: 'keen-slider',
            arrows: 'slider__arrows',
            arrow: 'slider__arrow',
            prev: 'slider__arrow--prev',
            next: 'slider__arrow--next',
            hasLoop: 'has-loop',
            hasAutoplay: 'has-autoplay',
            hasNavigation: 'has-navigation',
            hasPagination: 'has-pagination',
            hasCustomLoop: 'has-custom-loop', // Add manually
            hasDrag: 'has-drag',
            hasCenterWhenDisabled: 'has-center-when-disabled',
            pagination: 'slider__pagination',
            paginationItem: 'slider__pagination-item',
            active: 'is-active',
            visible: 'is-visible',
            enabled: 'is-enabled',
            loaded: 'is-loaded',
            disabled: 'is-disabled',
            grabbing: 'is-grabbing',
        };

        if (!target) {
            return false;
        }

        this.element = $(target);

        if (this.element.find('.slider__item').length === 1) {
            return;
        }

        this.arrows         = this.element.find(`.${this.classes.arrows}`);
        this.arrow          = this.element.find(`.${this.classes.arrow}`);
        this.prev           = this.element.find(`.${this.classes.prev}`);
        this.next           = this.element.find(`.${this.classes.next}`);
        this.paginationItem = this.element.find(`.${this.classes.paginationItem}`);
        this.keenSlider     = this.element.find(`.${this.classes.keenSlider}`);
        this.isFader        = this.element.hasClass(this.classes.sliderFade);

        // Autoplay
        this.timeout      = null;
        this.mouseOver    = false;
        this.slidesCount  = null;
        this.sliderObject = {};

        this.sliderSettings = {
            disabled: false,
            mode: 'snap',
            selector: `.${this.classes.item}`,
            slides: {
                perView: 1,
                spacing: 24,
            },
            // breakpoints: {
            //     '(max-width: 1024px)': {
            //         disabled: false,
            //         slides: {
            //             perView: 2,
            //             spacing: 24,
            //         },
            //     },
            // },
            enableAutoplay: false, // Custom property
            autoplayDelay: 2000, // Custom property
            centerWhenDisabled: true, // Custom property
        };

        if (options) {
            this.sliderSettings = { ...this.sliderSettings, ...options };
        }

        if (this.isFader) {
            this.sliderSettings.renderMode       = 'custom';
            this.sliderSettings.defaultAnimation = {
                duration: 3000,
            };
        }

        if (this.element.find(`.${this.classes.item}`).length <= 1) {
            this.sliderSettings.enableAutoplay = false;
            this.sliderSettings.loop           = false;
        }

        this.sliderEvents = [
            (slider) => {
                slider.on('optionsChanged', () => {
                    this.slidesCount = this.getSlidesCount(slider);

                    this.enabledState(this.element, slider);
                    this.hasNavigationEnabled(slider, this.arrows);
                    this.activeSlide(slider);
                    this.getVisibleSlides(slider);
                    this.getVisibleSlidesIndexes(slider);
                    this.setVisibleSlidesClasses(slider);
                    this.setGap();

                    this.sliderObject = slider;
                });

                slider.on('updated', () => {
                    this.slidesCount = this.getSlidesCount(slider);

                    if (this.slidesCount <= 1) {
                        this.enableAutoplay = false;
                    }

                    this.enabledState(this.element, this.keenSlider);
                    this.hasNavigationEnabled(slider, this.arrows);
                    this.insertPagination(slider, this.element);
                    this.activeSlide(slider);
                    this.getVisibleSlides(slider);
                    this.getVisibleSlidesIndexes(slider);
                    this.setVisibleSlidesClasses(slider);
                    this.setGap();
                    this.nextTimeout(); // Autoplay

                    this.sliderObject = slider;
                });

                slider.on('created', () => {
                    this.slidesCount = this.getSlidesCount(slider);

                    if (this.slidesCount <= 1) {
                        this.enableAutoplay = false;
                    }

                    this.initAutoplay(); // Autoplay
                    slider.update(); // Needed for pagination index fix
                    this.element.addClass(this.classes.loaded);
                    this.sliderSettings.loop && this.element.addClass(this.classes.hasLoop);
                    this.sliderSettings.drag && this.element.addClass(this.classes.hasDrag);
                    this.sliderSettings.centerWhenDisabled && this.element.addClass(this.classes.hasCenterWhenDisabled);
                });

                slider.on('slideChanged', () => {
                    this.paginationActiveState(slider, this.element);
                    this.arrowStates(slider, this.prev, this.next);
                    this.activeSlide(slider);
                    this.getVisibleSlides(slider);
                    this.getVisibleSlidesIndexes(slider);
                    this.setVisibleSlidesClasses(slider);

                    this.sliderObject = slider;
                });

                slider.on('detailsChanged', (sliderItem) => {
                    if (this.isFader) {
                        sliderItem.slides.forEach((element, idx) => {
                            element.style.opacity = slider.track.details.slides[idx].portion;
                        });
                    }
                });

                slider.on('dragStarted', () => {
                    this.keenSlider.addClass(this.classes.grabbing);

                    if (this.sliderSettings.enableAutoplay) {
                        this.clearNextTimeout();
                    }
                });

                slider.on('dragEnded', () => {
                    this.keenSlider.removeClass(this.classes.grabbing);
                });

                // Autoplay
                if (this.sliderSettings.enableAutoplay) {
                    slider.on('animationEnded', this.nextTimeout.bind(this));
                }
            },
        ];

        this.slider = new KeenSlider(this.keenSlider.get(0), this.sliderSettings, this.sliderEvents);

        this.arrow.on('click', this.onArrowClick.bind(this));

        return this.slider;
    }

    sliderTrackDetails(details) {
        return details || false;
    }

    // Available slides for animation (max page index)
    getSlidesCount(slider) {
        const isLoop      = slider.track.details.maxIdx === Infinity;
        const maxIndex    = isLoop ? slider.track.details.slides.length : slider.track.details.maxIdx;
        const slidesCount = Math.floor(isLoop ? maxIndex : maxIndex + 1);

        if (slidesCount <= 1) {
            return 0;
        }

        return slidesCount;
    }

    initAutoplay() {
        if (!this.sliderSettings.enableAutoplay) {
            return;
        }

        this.element.addClass(this.classes.hasAutoplay);
        this.element.get(0).style.setProperty('--slider-autoplay-timer', `${this.sliderSettings.autoplayDelay}ms`);

        this.keenSlider.on('mouseover', () => {
            this.mouseOver = true;
            this.clearNextTimeout();
        });
        this.keenSlider.on('mouseout', () => {
            this.mouseOver = false;
            this.nextTimeout();
        });
        this.nextTimeout();
    }

    clearNextTimeout() {
        clearTimeout(this.timeout);
    }

    nextTimeout() {
        if (!this.sliderSettings.enableAutoplay) {
            return;
        }

        clearTimeout(this.timeout);

        if (this.mouseOver) {
            return;
        }

        this.timeout = setTimeout(() => {
            this.slider.next();
        }, this.sliderSettings.autoplayDelay);
    }

    // resizeHander() {
    //     this.arrowsPosition();
    // }

    setGap() {
        const gap = this.sliderSettings.slides.spacing;

        this.element.get(0).style.setProperty('--slider-gap', `${gap}px`);
    }

    onPaginationClick(event) {
        const target = $(event.currentTarget);

        if (target.hasClass(this.classes.active)) {
            return event;
        }

        const index = parseInt(target.attr('data-index'));

        this.slider.moveToIdx(index);
    }

    paginationActiveState(slider, target) {
        const index = this.sliderTrackDetails(slider.track.details) ? slider.track.details.rel : 0;

        target.find(`.${this.classes.paginationItem}`).removeClass(this.classes.active);
        target.find(`.${this.classes.paginationItem}[data-index="0"]`).text(index + 1);
        target.find(`.${this.classes.paginationItem}[data-index="${index}"]`).addClass(this.classes.active);
    }

    isDisabled() {
        return this.slidesCount <= 0;
    }

    enabledState(target) {
        target[this.isDisabled() ? 'removeClass' : 'addClass'](this.classes.enabled);
    }

    isBeginning(slider) {
        return slider.track.details.rel === 0;
    }

    isEnd(slider) {
        return slider.track.details.rel === this.getSlidesCount(slider) - 1;
    }

    insertPagination(slider, target) {
        const hasPagination = target.find(`.${this.classes.pagination}`).length;

        if (!hasPagination) {
            return slider;
        }

        const details = this.sliderTrackDetails(slider.track.details);

        if (!details) {
            this.element.removeClass(this.classes.hasPagination);

            return slider;
        }

        if (this.slidesCount <= 1) {
            this.element.removeClass(this.classes.hasPagination);

            return slider;
        }

        this.element.addClass(this.classes.hasPagination);

        if (this.element.find(`.${this.classes.paginationItem}`).length) {
            this.element.find(`.${this.classes.paginationItem}:last-child`).text(this.slidesCount).attr('data-index', this.slidesCount - 1);

            this.element.find(`.${this.classes.paginationItem}:last-child`).off('click');
            this.element.find(`.${this.classes.paginationItem}:last-child`).on('click', this.onPaginationClick.bind(this));

            return slider;
        }

        for (let index = 0; index < this.slidesCount; index++) {
            const realIndex = index + 1;

            // if (realIndex > 1 && realIndex < this.slidesCount) {
            //     continue;
            // }

            const dotButton = $(`<button
                class="${this.classes.paginationItem}"
                tabindex="-1"
                data-index="${index}">
                    ${this.slidesCount === realIndex ? this.slidesCount : realIndex}
                </button>`);

            dotButton.on('click', this.onPaginationClick.bind(this));

            target.find(`.${this.classes.pagination}`).append(dotButton);
            target.find(`.${this.classes.paginationItem}:first-child`).addClass(this.classes.active);
        }
    }

    hasNavigationEnabled() {
        this.element[this.isDisabled() ? 'removeClass' : 'addClass'](this.classes.hasNavigation);
    }

    // arrowsPosition() {
    //     const windowWidth = window.innerWidth;

    //     if (windowWidth <= 1024) {
    //         this.arrows.detach().insertAfter(this.keenSlider.parent());
    //     } else {
    //         this.arrows.detach().appendTo(this.lineContent);
    //     }
    // }

    arrowStates(slider, prevTarget, nextTarget) {
        if (this.sliderSettings.loop) {
            return;
        }

        prevTarget[this.isBeginning(slider) ? 'addClass' : 'removeClass'](this.classes.disabled);
        nextTarget[this.isEnd(slider) ? 'addClass' : 'removeClass'](this.classes.disabled);
    }

    onArrowClick(event) {
        const target = $(event.currentTarget);
        const isNext = target.hasClass(this.classes.next);

        if (this.element.hasClass(this.classes.hasCustomLoop) && this.isEnd(this.sliderObject)) {
            this.slider.moveToIdx(0, false, {
                duration: 100,
            });

            return;
        }

        this.slider[isNext ? 'next' : 'prev']();
    }

    activeSlide(slider) {
        const current = this.sliderTrackDetails(slider.track.details) ? slider.track.details.rel : 0;

        if (current < 0) {
            return slider;
        }

        const slideItem = this.element.find(`.${this.classes.item}`);

        slideItem.removeClass(this.classes.active);
        slideItem.eq(current).addClass(this.classes.active);
    }

    getVisibleSlides(slider) {
        if (!this.sliderTrackDetails(slider.track.details)) {
            return false;
        }

        return slider.track.details.slides.filter((item) => item.portion > 0.4);
    }

    getVisibleSlidesIndexes(slider) {
        const visibleSlides = this.getVisibleSlides(slider);

        if (!visibleSlides) {
            return false;
        }

        return visibleSlides.map((slide) => slide.abs);
    }

    setVisibleSlidesClasses(slider) {
        this.element.find(`.${this.classes.item}`).removeClass(this.classes.visible);

        const visibleIndexes = this.getVisibleSlidesIndexes(slider);

        if (visibleIndexes) {
            visibleIndexes.map((index) => {
                this.element.find(`.${this.classes.item}:nth-child(${index + 1})`).addClass(this.classes.visible);
            });
        }
    }
}

// Init on document ready
window.sliders = [];

$(() => {
    $('.js-slider').map(function() {
        window.sliders.push(new Slider(this));
    });
});

/**
 * Extending current Slider class example:
 *
 * import { Slider } from '../slider/slider';

 * $(() => {
 *   $('.js-product-slider').map(function() { // Make sure you don't have js-slider class on the element
 *       this.slider = new Slider(this, {
 *           enableAutoplay: true,
 *       });
 *   });
 * });
 */
